import { useEffect, RefObject } from 'react';
/**
 * React hook that triggers a callback function when an element
 * is click outside the reference element
 *
 * @param ref - Is a react reference object
 * @param callback - Function that gets called
 *
 */
export function useOutsideClick(
	ref: RefObject<HTMLElement | null>,
	callback: (event: MouseEvent) => void,
	ignoreId: string,
) {
	useEffect(() => {
		if (!ref) {
			return;
		}
		/**
		 * Alert if clicked on outside of element
		 */
		function handleClickOutside(event: MouseEvent) {
			const target = event.target as HTMLElement | null;

			if (ref.current && !ref.current.contains(event.target as Node)) {
				// The menu's own toggle button: its click handler already
				// toggles the menu, so treating the press as an outside click
				// too would close it twice (or close-then-reopen). Matched with
				// `closest` because the press usually lands on the button's
				// icon, not the button itself.
				if (
					target?.closest?.(
						`[data-menu-toggle]${ignoreId ? `, #${ignoreId}` : ''}`,
					)
				) {
					return;
				}
				callback(event);
			}
		}
		// Bind the event listener
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			// Unbind the event listener on clean up
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [ref, callback, ignoreId]);
}
